import { MapCalculationMode, MapLayerType } from '../enums';
import type { Extent } from 'ol/extent';
import type { ColorMap } from './color-map.model';

export const MAP_PROJECTION_3857 = 'EPSG:3857';
export const MAP_PROJECTION_4326 = 'EPSG:4326';
export const MAP_CENTER_HUNGARY_3857: [number, number] = [2158869.0383998896, 5995841.283502453];
export const MAP_ZOOM_DEFAULT = 7.5;

export const MAP_BG_LAYER_URL_DEFAULT =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

export interface Map {
  id: string;
  projection: string;
  center: [number, number];
  zoom: number;
  loadTilesWhileAnimating: boolean;
  loadTilesWhileInteracting: boolean;
  mapSourceUrl?: string;
  attributions?: string;
  mapSourcePreload?: number;
  zoneInteractionEnabled: boolean;
  layers: MapLayerGroup[];
  extent: Extent;
  fitExtent?: Extent;
  fitDuration: number;
  fitPadding: number;
  calculationMode: MapCalculationMode;
  mapInfoEnabled: boolean;
  mapInfos: MapInfo[];
  connectedMaps: Map[];
  firstMapLayerLoaded: boolean;
  showId: boolean;
  autoFitOnFirstLayerLoad: boolean;
  coordinatesEnabled: boolean | 'hover';
  coordinatesProjection: string;
  coordinatesPrecision: number;
  scaleLineEnabled: boolean;
  scaleLineUnits: 'metric' | 'imperial' | 'degrees' | 'us' | 'nautical';
  t: (key: string) => string;
}

export interface MapConfig {
  id: string;
  projection: string;
  center: [number, number];
  zoom: number;
  loadTilesWhileAnimating: boolean;
  loadTilesWhileInteracting: boolean;
  mapSourceUrl?: string;
  attributions?: string;
  mapSourcePreload?: number;
  zoneInteractionEnabled: boolean;
  fitExtent?: Extent;
  fitDuration: number;
  fitPadding: number;
  mapInfoEnabled: boolean;
  showId: boolean;
  autoFitOnFirstLayerLoad: boolean;
  coordinatesEnabled: boolean | 'hover';
  coordinatesProjection: string;
  coordinatesPrecision: number;
  scaleLineEnabled: boolean;
  scaleLineUnits: 'metric' | 'imperial' | 'degrees' | 'us' | 'nautical';
  calculationMode: MapCalculationMode;
}

export interface MapLayer<TData extends MapLayerData = MapLayerData> {
  mapLayerType: MapLayerType;
  type: string;
  id: string;
  name: string;
  data: TData;
  enabled: boolean;
  opacity: number;
  extent: Extent;
  loading: boolean;
  loadingPromise?: Promise<void>;
  customData: any;
  generateColorInfo?: (values: number[], colorMap: ColorMap) => Promise<any>;
  mapInfo: {
    doNotGenerate: boolean;
    enabled: boolean;
    titleFn: (layer: MapLayer) => string;
    subtitleFn: (layer: MapLayer, t: (key: string) => string) => string;
    valueFn: (layer: MapLayer, title: string, i?: number) => string;
    colorBar: boolean;
  };
  group?: MapLayerGroup<TData>;
}

export interface MapLayerGroup<TData extends MapLayerData = MapLayerData> {
  mapLayerType: MapLayerType;
  id: string;
  name: string;
  map?: Map;
  subLayers: MapLayer<TData>[];
  activeSubLayer: MapLayer<TData>;
  customData: any;
  zIndex: number;
}

export interface MapLayerConfig<TRawData> {
  type: string;
  id: string;
  rawData: TRawData;
  classification: string;
  name: string;
  unitType?: string;
  unitTypeName?: string;
  colorMap?: ColorMap;
  enabled?: boolean;
  opacity?: number;
  extent?: Extent;
  customData?: any;
  generateColorInfo?: (values: number[], colorMap: ColorMap) => Promise<any>;
  mapInfo?: {
    doNotGenerate?: boolean;
    enabled?: boolean;
    titleFn?: (layer: MapLayer) => string;
    subtitleFn?: (layer: MapLayer, t: (key: string) => string) => string;
    valueFn?: (layer: MapLayer, title: string, i?: number) => string;
    colorBar?: boolean;
  };
}

export interface MapLayerGroupConfig<TLayerConfig> {
  id: string;
  name: string;
  mapLayerConfigs: TLayerConfig[];
  customData?: any;
}

export interface MapLayerData {
  classification: string;
  unitType?: string;
  unitTypeName?: string;
  colorMap?: ColorMap;
  values: number[];
}

export interface MapInfo {
  layerId: string;
  layerType: string;
  enabled: boolean;
  title: string;
  subtitle: string;
  values: MapInfoValue[];
  colorBar: boolean;
}

export interface MapInfoValue {
  title: string;
  color: string;
  percent: number;
}

export type MapInfoConfig = MapInfo;
